import { ALEXANDRIA_METRICS, MetricSeriesCompany, MetricSeriesPoint } from 'dashboard409a/services/alexandria/types';
import { COMPUTED_METRICS } from '../constants';

// Pure transforms from Alexandria's flat point list into the shapes the
// Public Comps widgets need. No React, no fetching — unit-testable.

export interface SeriesPoint {
  date: string; // YYYY-MM-DD (requested_date grid)
  value: number;
  // Real observation date, present only when it differs from the grid date
  // (the value was carried forward).
  asOfDate?: string;
}

// requested identifier -> metric_key -> points sorted by date.
export type MetricSeriesByCompany = Record<string, Record<string, SeriesPoint[]>>;

// Growth cohorts used by the Multiples chart (thresholds on NTM revenue
// growth). Alexandria returns ratio metrics as percent values (57.85 means
// 57.85%), so thresholds are in percent points.
const HIGH_GROWTH_MIN = 40;
const MEDIUM_GROWTH_MIN = 20;

type GrowthCohort = 'high' | 'medium' | 'low';

export const groupPoints = (points: MetricSeriesPoint[]): MetricSeriesByCompany => {
  const byCompany: MetricSeriesByCompany = {};
  points.forEach(point => {
    if (point.value === null) {
      return;
    }
    const value = parseFloat(point.value);
    if (Number.isNaN(value)) {
      return;
    }

    const key = point.requested_identifier;
    const metrics = byCompany[key] ?? {};
    const series = metrics[point.metric_key] ?? [];
    const carriedForward = point.as_of_date && point.as_of_date !== point.requested_date;
    series.push({
      date: point.requested_date,
      value,
      ...(carriedForward ? { asOfDate: point.as_of_date as string } : {}),
    });
    metrics[point.metric_key] = series;
    byCompany[key] = metrics;
  });

  Object.values(byCompany).forEach(metrics => {
    Object.values(metrics).forEach(series => series.sort((a, b) => a.date.localeCompare(b.date)));
  });
  return byCompany;
};

// requested identifier -> company name, returned once per company.
export const extractCompanyNames = (companies: MetricSeriesCompany[]): Record<string, string> => {
  const names: Record<string, string> = {};
  companies.forEach(company => {
    if (company.company_name) {
      names[company.requested_identifier] = company.company_name;
    }
  });
  return names;
};

const toDateMap = (series: SeriesPoint[]): Map<string, number> => new Map(series.map(p => [p.date, p.value]));

// Scalar defines NTM revenue growth as (NTM revenue / LTM revenue) - 1.
// Store percent points because the Public Comps percentage widgets consume
// values in the same shape as Alexandria ratios (11.2 means 11.2%).
export const withNtmRevenueGrowth = (byCompany: MetricSeriesByCompany): MetricSeriesByCompany =>
  Object.fromEntries(
    Object.entries(byCompany).map(([companyId, metrics]) => {
      const ltmRevenueByDate = toDateMap(metrics[ALEXANDRIA_METRICS.LTM_REVENUE] ?? []);
      const ntmRevenueGrowth = (metrics[ALEXANDRIA_METRICS.NTM_REVENUE] ?? []).flatMap(point => {
        const ltmRevenue = ltmRevenueByDate.get(point.date);
        if (point.value === 0 || ltmRevenue === undefined || ltmRevenue === 0) {
          return [];
        }
        return [{ date: point.date, value: (point.value / ltmRevenue - 1) * 100 }];
      });

      return [
        companyId,
        ntmRevenueGrowth.length
          ? { ...metrics, [COMPUTED_METRICS.NTM_REVENUE_GROWTH]: ntmRevenueGrowth }
          : metrics,
      ];
    })
  );

// EV / denominator per company, on dates where both metrics have values and
// the denominator is positive (multiples on non-positive bases are noise).
export const computeMultipleSeries = (
  byCompany: MetricSeriesByCompany,
  denominatorKey: string,
  numeratorKey: string = ALEXANDRIA_METRICS.ENTERPRISE_VALUE
): Record<string, SeriesPoint[]> => {
  const result: Record<string, SeriesPoint[]> = {};
  Object.entries(byCompany).forEach(([companyId, metrics]) => {
    const numerator = metrics[numeratorKey] ?? [];
    const denominator = toDateMap(metrics[denominatorKey] ?? []);
    const series = numerator
      .filter(p => (denominator.get(p.date) ?? 0) > 0)
      .map(p => ({ date: p.date, value: p.value / denominator.get(p.date)! }));
    if (series.length) {
      result[companyId] = series;
    }
  });
  return result;
};

export const median = (values: number[]): number | null => {
  if (!values.length) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// Median across companies for every date where at least one company has a value.
export const medianSeries = (seriesByCompany: Record<string, SeriesPoint[]>): SeriesPoint[] => {
  const valuesByDate = new Map<string, number[]>();
  Object.values(seriesByCompany).forEach(series => {
    series.forEach(({ date, value }) => {
      const values = valuesByDate.get(date) ?? [];
      values.push(value);
      valuesByDate.set(date, values);
    });
  });
  return [...valuesByDate.entries()]
    .map(([date, values]) => ({ date, value: median(values)! }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

// Percent change (in points, 12 === +12%) versus the first point of the window.
export const rateOfChangeSeries = (series: SeriesPoint[]): SeriesPoint[] => {
  const base = series.find(p => p.value !== 0);
  if (!base) {
    return [];
  }
  return series
    .filter(p => p.date >= base.date)
    .map(p => ({ date: p.date, value: ((p.value - base.value) / Math.abs(base.value)) * 100 }));
};

export const latestValue = (series: SeriesPoint[] | undefined): number | null =>
  series?.length ? series[series.length - 1].value : null;

export const latestPoint = (series: SeriesPoint[] | undefined): SeriesPoint | null =>
  series?.length ? series[series.length - 1] : null;

export const classifyGrowth = (growth: number | null): GrowthCohort | null => {
  if (growth === null) {
    return null;
  }
  if (growth >= HIGH_GROWTH_MIN) {
    return 'high';
  }
  if (growth >= MEDIUM_GROWTH_MIN) {
    return 'medium';
  }
  return 'low';
};

// Buckets companies by the latest value of the selected percent-growth series.
export const growthCohorts = (
  byCompany: MetricSeriesByCompany,
  growthMetric: string = ALEXANDRIA_METRICS.ESTIMATED_REVENUE_GROWTH
): Record<GrowthCohort, string[]> => {
  const cohorts: Record<GrowthCohort, string[]> = { high: [], medium: [], low: [] };
  Object.entries(byCompany).forEach(([companyId, metrics]) => {
    const cohort = classifyGrowth(latestValue(metrics[growthMetric]));
    if (cohort) {
      cohorts[cohort].push(companyId);
    }
  });
  return cohorts;
};

export const pickCompanies = (
  seriesByCompany: Record<string, SeriesPoint[]>,
  companyIds: string[]
): Record<string, SeriesPoint[]> =>
  Object.fromEntries(companyIds.filter(id => seriesByCompany[id]).map(id => [id, seriesByCompany[id]]));

export interface StatWithMoM {
  current: number | null;
  previous: number | null; // median ~1 month before the latest date
  // The two dates being compared, for display.
  currentDate: string | null;
  previousDate: string | null;
}

const shiftDateByDays = (isoDate: string, days: number): string => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

// Median now vs median one month ago, for the State of Market stat cards.
export const statWithMoM = (seriesByCompany: Record<string, SeriesPoint[]>): StatWithMoM => {
  const medians = medianSeries(seriesByCompany);
  if (!medians.length) {
    return { current: null, previous: null, currentDate: null, previousDate: null };
  }

  const latest = medians[medians.length - 1];
  const target = shiftDateByDays(latest.date, -30);
  const previous = [...medians].reverse().find(p => p.date <= target) ?? null;

  return {
    current: latest.value,
    previous: previous ? previous.value : null,
    currentDate: latest.date,
    previousDate: previous ? previous.date : null,
  };
};
